"use client";

import { useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

export type ChangelogEntry = {
  version: string;
  date: string;
  title: string;
  description: string;
  items?: string[];
  image?: string;
};

export interface Changelog8Props {
  title?: string;
  description?: string;
  entries?: ChangelogEntry[];
  className?: string;
}

const Changelog8 = ({
  title = "Changelog",
  description = "Get the latest updates and improvements to our platform.",
  entries = defaultChangelogData,
  className,
}: Changelog8Props) => {
  const [activeId, setActiveId] = useState<number | null>(null);
  const sectionRefs = useRef<Array<HTMLDivElement | null>>([]);

  useEffect(() => {
    sectionRefs.current = sectionRefs.current.slice(0, entries.length);

    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150; // Adding offset to improve usability

      // Find the section that is currently in view
      let currentSectionIndex = -1;

      for (let i = 0; i < sectionRefs.current.length; i++) {
        const ref = sectionRefs.current[i];
        if (!ref) continue;

        const refTop = ref.offsetTop;
        const refBottom = ref.offsetTop + ref.offsetHeight;
        const nextRef = sectionRefs.current[i + 1];

        // For the last section, check if we're past its start
        if (i === sectionRefs.current.length - 1) {
          if (scrollPosition >= refTop - 100) {
            currentSectionIndex = i;
            break;
          }
        } else {
          // For other sections, check if we're in their range
          const nextTop = nextRef ? nextRef.offsetTop : refBottom;
          if (
            scrollPosition >= refTop - 100 &&
            scrollPosition < nextTop - 100
          ) {
            currentSectionIndex = i;
            break;
          }
        }
      }

      if (currentSectionIndex !== -1) {
        setActiveId(currentSectionIndex);
      } else if (
        scrollPosition <
        (sectionRefs.current[0]?.offsetTop || 0) - 100
      ) {
        setActiveId(null);
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Initialize on mount

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [entries.length]);

  const scrollToSection = (index: number) => {
    const sectionRef = sectionRefs.current[index];
    if (sectionRef) {
      const offset = 100; // Match the scroll detection offset
      window.scrollTo({
        top: sectionRef.offsetTop - offset,
        behavior: "smooth",
      });
      // Update active state immediately to prevent flickering
      setActiveId(index);
    }
  };

  // Correct way to set refs for linter
  const setRef = (index: number) => (el: HTMLDivElement | null) => {
    sectionRefs.current[index] = el;
  };

  return (
    <section className={cn("bg-background py-24 md:py-32", className)}>
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative flex flex-col lg:flex-row">
        {/* Header for mobile */}
        <div className="mb-12 lg:hidden">
          <h1 className="mb-4 text-3xl font-bold tracking-tight md:text-4xl">
            {title}
          </h1>
          <p className="text-muted-foreground text-base md:text-lg">
            {description}
          </p>
          <Separator className="mt-8" />
        </div>

        {/* Scrollspy sidebar */}
        <div className="sticky top-24 hidden h-fit w-80 overflow-visible pr-10 lg:block">
          <div className="mb-10">
            <h1 className="mb-3 text-3xl font-bold tracking-tight">{title}</h1>
            <p className="text-muted-foreground text-base">{description}</p>
          </div>
          <Separator className="mb-6" />
          <ScrollArea className="h-[calc(100vh-300px)]">
            <div className="pb-6">
              <h3 className="text-muted-foreground mb-5 text-sm font-medium uppercase tracking-wider">
                On this page
              </h3>
              <nav className="flex flex-col space-y-2.5">
                {entries.map((entry, index) => (
                  <button
                    type="button"
                    key={entry.version}
                    onClick={() => scrollToSection(index)}
                    className={cn(
                      "hover:border-border hover:bg-accent group flex flex-col gap-2 rounded-md border border-transparent px-3 py-2 text-left transition-all",
                      activeId === index
                        ? "border-border bg-accent text-accent-foreground font-medium shadow-sm"
                        : "text-muted-foreground",
                    )}
                  >
                    <span className="text-sm">{entry.title}</span>
                    <div className="text-muted-foreground flex items-center gap-2 text-xs">
                      <span className="font-medium">{entry.version}</span>
                      <span>•</span>
                      <span>{entry.date}</span>
                    </div>
                  </button>
                ))}
              </nav>
            </div>
          </ScrollArea>
        </div>

        {/* Main content */}
        <div className="flex-1 lg:pl-10">
          <div className="mb-10 hidden lg:block">
            <h2 className="text-xl font-semibold">Latest updates</h2>
          </div>
          <div className="space-y-16 md:space-y-24">
            {entries.map((entry, index) => (
              <div
                key={entry.version}
                ref={setRef(index)}
                className="border-l-border/30 hover:border-l-border relative flex flex-col gap-6 border-l-4 pl-6 transition-colors md:gap-8"
                id={`entry-${index}`}
              >
                <div className="flex flex-wrap items-center gap-3">
                  <Badge
                    variant="secondary"
                    className="px-3 py-1 text-xs font-medium"
                  >
                    {entry.version}
                  </Badge>
                  <span className="text-muted-foreground text-xs font-medium">
                    {entry.date}
                  </span>
                </div>
                <div>
                  <h2 className="text-foreground mb-4 text-xl font-bold leading-tight md:text-2xl">
                    {entry.title}
                  </h2>
                  <p className="text-muted-foreground mb-6 text-base md:text-base">
                    {entry.description}
                  </p>
                  {entry.items && entry.items.length > 0 && (
                    <div className="bg-muted/30 mt-6 rounded-lg border p-5">
                      <h3 className="mb-3 text-sm font-medium">
                        What's included:
                      </h3>
                      <ul className="text-muted-foreground space-y-2 text-sm">
                        {entry.items.map((item) => (
                          <li
                            key={`${entry.version}-${item}`}
                            className="flex items-start"
                          >
                            <span className="bg-primary mr-2 mt-1 h-1.5 w-1.5 shrink-0 rounded-full" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {entry.image && (
                    <div className="mt-8 overflow-hidden rounded-xl border">
                      <div className="relative w-full aspect-video flex items-center justify-center bg-muted/30 p-12 md:p-16 lg:p-20">
                        <img
                          src={entry.image}
                          alt={`${entry.version} visual`}
                          className="max-w-[60%] max-h-[60%] w-auto h-auto object-contain transition-transform hover:scale-105"
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export { Changelog8 };

export const defaultChangelogData: ChangelogEntry[] = [
  {
    version: "Version 1.3.0",
    date: "15 November 2024",
    title: "Enhanced Analytics Dashboard",
    description:
      "We've completely redesigned our analytics dashboard to provide deeper insights and improved visualizations of your data.",
    items: [
      "Interactive data visualizations with real-time updates",
      "Customizable dashboard widgets",
      "Export analytics in multiple formats (CSV, PDF, Excel)",
      "New reporting templates for common use cases",
      "Improved data filtering and segmentation options",
    ],
    image:
      "https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-aspect-video-1.svg",
  },
  {
    version: "Version 1.2.5",
    date: "7 October 2024",
    title: "Mobile App Launch",
    description:
      "We're excited to announce the launch of our mobile application, available now on iOS and Android platforms.",
    items: [
      "Native mobile experience for on-the-go productivity",
      "Offline mode support for working without internet connection",
      "Push notifications for important updates",
      "Biometric authentication for enhanced security",
    ],
    image:
      "https://deifkwefumgah.cloudfront.net/shadcnblocks/block/placeholder-aspect-video-1.svg",
  },
];
